import type { MembershipRole } from "@safeplate/contracts";

import { ApiError } from "../api/errors.js";
import { pinProblem as pinRule } from "../quick-access/pin.js";

// "logins": the manager sets each person's password and the login works at
// once. "invite": each person gets an email and sets their own password.
export type AddMode = "invite" | "logins";

export const PASSWORD_MIN = 10;
export const PASSWORD_MAX = 128;

export interface NewPerson {
  email: string;
  name: string;
  password: string;
  pin: string;
  role: MembershipRole;
}

export function blankPerson(role: MembershipRole = "staff"): NewPerson {
  return { email: "", name: "", password: "", pin: "", role };
}

// A PIN only helps someone who can sign in, so it needs an email: nobody
// uses SafePlate on a PIN alone.
export function pinProblem(person: NewPerson): string | null {
  if (!person.pin || !person.email.trim()) return null;
  return pinRule(person.pin);
}

export function passwordProblem(
  person: NewPerson,
  mode: AddMode,
): string | null {
  if (mode !== "logins" || !person.email.trim() || !person.password)
    return null;
  return person.password.length < PASSWORD_MIN
    ? `Password must be at least ${PASSWORD_MIN} characters`
    : null;
}

export function rowProblem(person: NewPerson, mode: AddMode): string | null {
  return passwordProblem(person, mode) ?? pinProblem(person);
}

// With logins, everyone with an email needs a password before saving.
export function readyToAdd(rows: readonly NewPerson[], mode: AddMode): boolean {
  const named = namedPeople(rows);
  return (
    named.length > 0 &&
    named.every(
      (row) =>
        rowProblem(row, mode) === null &&
        (mode !== "logins" || !row.email.trim() || row.password !== ""),
    )
  );
}

export function namedPeople(rows: readonly NewPerson[]): NewPerson[] {
  return rows.filter((row) => row.name.trim() !== "");
}

export interface AddPeopleDeps {
  createStaff: (input: {
    display_name: string;
    email: string | null;
    job_title: null;
    location_ids: string[];
    quick_pin?: string;
  }) => Promise<{ id: string }>;
  createStaffLogins: (
    people: {
      display_name: string;
      email: string;
      location_ids: string[];
      password: string;
      quick_pin?: string;
      role: MembershipRole;
    }[],
  ) => Promise<unknown>;
  inviteStaff: (input: {
    email: string;
    location_ids: string[];
    role: MembershipRole;
    staff_profile_id: string;
  }) => Promise<unknown>;
}

// What the manager hands over, shown once.
export interface LoginDetails {
  email: string;
  name: string;
  password: string;
  pin: string | null;
}

export interface AddPeopleResult {
  added: number;
  // Rows that were not added, ready to try again. The error is null for a
  // person held back only because someone else in the same batch failed.
  failed: { error: unknown; person: NewPerson }[];
  invited: number;
  logins: LoginDetails[];
  notInvited: string[];
}

export const LOGINS_PER_REQUEST = 20;

// "people.3.email" names the fourth person of the batch.
function failedIndex(error: unknown): number | null {
  if (!(error instanceof ApiError) || !error.field) return null;
  const match = /^people\.(\d+)(\.|$)/.exec(error.field);
  return match ? Number(match[1]) : null;
}

// Adds each named person. With logins, everyone with an email gets a
// working login in batches that succeed or fail as a whole; otherwise they
// are added and invited one by one. One failure never stops the rest.
export async function addPeople(
  rows: readonly NewPerson[],
  locationIds: readonly string[],
  mode: AddMode,
  deps: AddPeopleDeps,
): Promise<AddPeopleResult> {
  const result: AddPeopleResult = {
    added: 0,
    failed: [],
    invited: 0,
    logins: [],
    notInvited: [],
  };
  const named = namedPeople(rows);
  const failures = new Map<NewPerson, unknown>();
  const withLogin =
    mode === "logins" ? named.filter((person) => person.email.trim()) : [];
  for (let start = 0; start < withLogin.length; start += LOGINS_PER_REQUEST) {
    const batch = withLogin.slice(start, start + LOGINS_PER_REQUEST);
    try {
      await deps.createStaffLogins(
        batch.map((person) => ({
          display_name: person.name.trim(),
          email: person.email.trim(),
          location_ids: [...locationIds],
          password: person.password,
          role: person.role,
          ...(person.pin ? { quick_pin: person.pin } : {}),
        })),
      );
    } catch (error) {
      // An index outside the batch names nobody: show the error on everyone.
      const named = failedIndex(error);
      const index = named !== null && named < batch.length ? named : null;
      batch.forEach((person, position) =>
        failures.set(
          person,
          index === null || index === position ? error : null,
        ),
      );
      continue;
    }
    result.added += batch.length;
    result.logins.push(
      ...batch.map((person) => ({
        email: person.email.trim(),
        name: person.name.trim(),
        password: person.password,
        pin: person.pin || null,
      })),
    );
  }
  for (const person of named) {
    if (withLogin.includes(person)) continue;
    const name = person.name.trim();
    const email = person.email.trim();
    let id: string;
    try {
      ({ id } = await deps.createStaff({
        display_name: name,
        email: email || null,
        job_title: null,
        location_ids: [...locationIds],
        ...(email && person.pin ? { quick_pin: person.pin } : {}),
      }));
    } catch (error) {
      failures.set(person, error);
      continue;
    }
    result.added += 1;
    if (!email) continue;
    try {
      await deps.inviteStaff({
        email,
        location_ids: person.role === "admin" ? [] : [...locationIds],
        role: person.role,
        staff_profile_id: id,
      });
      result.invited += 1;
    } catch {
      result.notInvited.push(name);
    }
  }
  result.failed = named
    .filter((person) => failures.has(person))
    .map((person) => ({ error: failures.get(person), person }));
  return result;
}

// A retry adds to what earlier tries already did; failures are the latest.
export function combineResults(
  earlier: AddPeopleResult | null,
  latest: AddPeopleResult,
): AddPeopleResult {
  if (!earlier) return latest;
  return {
    added: earlier.added + latest.added,
    failed: latest.failed,
    invited: earlier.invited + latest.invited,
    logins: [...earlier.logins, ...latest.logins],
    notInvited: [...earlier.notInvited, ...latest.notInvited],
  };
}
